/**
 * Shared size and weight rules, used by the audit engine and the Tools page,
 * so both always agree on what "fits" means.
 */

const EPS = 1e-9;

/** Numbers sorted largest first, without changing the input. */
export function sortDesc(xs: readonly number[]): number[] {
  return [...xs].sort((a, b) => b - a);
}

/**
 * True when a parcel fits size and weight limits. Sides are compared longest to longest,
 * middle to middle and shortest to shortest, whatever order either list is in. Limits are inclusive.
 */
export function fitsWithin(sides: readonly number[], kg: number, maxSides: readonly number[], maxKg: number): boolean {
  if (kg > maxKg + EPS) return false;
  const a = sortDesc(sides);
  const b = sortDesc(maxSides);
  return a.every((d, i) => d <= (b[i] ?? 0) + EPS);
}

/**
 * True when a parcel does not fit, but every measure it is over (each side, and weight) is over
 * by no more than `tolerance` (0.1 = 10 per cent). The "near miss" rule.
 */
export function nearMiss(sides: readonly number[], kg: number, maxSides: readonly number[], maxKg: number, tolerance = 0.1): boolean {
  if (fitsWithin(sides, kg, maxSides, maxKg)) return false;
  const a = sortDesc(sides);
  const b = sortDesc(maxSides);
  const ratios = a.map((d, i) => d / (b[i] ?? Infinity)).concat(kg / maxKg);
  return ratios.every((r) => r <= 1 + tolerance + EPS);
}

/** Volumetric weight in kg, unrounded: length × width × height (cm) / divisor. */
export function volumetricKg(sides: readonly number[], divisor: number): number {
  return sides.reduce((p, s) => p * s, 1) / divisor;
}
